<template>
  <div :key="key">
    <slot name="label">
      <div v-show="!hideLabel && label">
        <FieldLabel
          :class="labelStyles || 'pl-1'"
          v-bind="{ label, tippy, id }"
        />
      </div>
    </slot>

    <div class="flex flex-wrap gap-2">
      <div class="flex-1 relative rounded-md">
        <div
          v-show="hardDisable"
          class="form-input w-full text-left opacity-25 cursor-not-allowed"
          :class="inputStyles"
        >
          {{ value || placeholder || '&nbsp;' }}
        </div>

        <!-- 
          Keep attributes for these 2 components the same, 
          do not turn into a dynamic <component>, 
          else the value will not set properly 
        -->
        <input
          v-if="!isTextArea"
          v-show="!hardDisable"
          ref="input"
          class="form-input w-full"
          :class="[inputStyles, textInputStyles, hideSpinnerClass]"
          data-hj-whitelist
          :aria-label="hideLabel && label"
          v-bind="{ ...$attrs, ...$props }"
          @blur="handleBlur($event)"
          @focus="handleFocus($event)"
          @enter="handleEnter($event)"
          @keyup.enter="handleKeyupEnter($event)"
          @input="handleInput($event)"
        />
        <textarea
          v-if="isTextArea"
          v-show="!hardDisable"
          ref="input"
          class="form-input w-full block"
          :class="[inputStyles, textInputStyles]"
          data-hj-whitelist
          :aria-label="hideLabel && label"
          v-bind="{ ...$attrs, ...$props }"
          @blur="handleBlur($event)"
          @focus="handleFocus($event)"
          @enter="handleEnter($event)"
          @keyup.enter="handleKeyupEnter($event)"
          @input="handleInput($event)"
        />

        <!-- Error/Success Icon -->
        <component
          :is="iconType"
          v-show="statusIconVisible"
          class="absolute inset-y-1/2 transform -translate-y-1/2 right-0 mr-3 pointer-events-none h-5 w-5"
          :class="{ 'text-green-500': isSuccess, 'text-red-500': isError }"
        />
      </div>

      <slot name="button"></slot>
    </div>

    <div
      v-if="prompt || showWordCount"
      class="flex gap-4 justify-end text-xs text-gray-500 mt-1 px-1"
    >
      <div v-show="prompt" class=" flex-1" v-html="prompt"></div>
      <div v-if="showWordCount && maxlength">
        <p class="text-right">{{ value.length }}/{{ maxlength }}</p>
      </div>
    </div>

    <FieldMessage
      :status-message="statusMessage"
      :is-success="isSuccess"
      :is-error="isError"
    />
  </div>
</template>

<script>
import ExclamationCircle from '@/assets/svg/icons/exclamation-circle.svg?inline'
import CheckCircle from '@/assets/svg/icons/check-circle.svg?inline'
import Spinner from '@/assets/svg/spinner.svg?inline'
import FieldMessage from '@/components/form/FieldMessage'
import FieldLabel from '@/components/form/FieldLabel'

export default {
  name: 'Input',

  components: {
    ExclamationCircle,
    CheckCircle,
    Spinner,
    FieldLabel,
    FieldMessage,
  },

  props: {
    type: {
      type: String,
      default: 'text',
      validator: value =>
        value.match(/(text|number|email|password|date|number|tel|url)/),
    },

    state: {
      type: String,
      default: null,
      validator: value => value.match(/(hover|active|focus)/),
    },

    id: {
      type: String,
      default() {
        return `input-${this._uid}`
      },
    },
    inputStyles: { type: String, default: '' },
    statusMessage: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    maxlength: { type: String, default: '' },
    minlength: { type: String, default: '' },
    prompt: { type: String, default: '' },
    status: { type: String, default: '' },
    hideLabel: { type: Boolean, default: false },
    spellcheck: { type: Boolean, default: false },
    label: { type: String, default: '' },
    value: { type: [String, Number], default: null },
    tippy: { type: String, default: '' },
    autofocus: Boolean,
    disabled: Boolean,
    required: Boolean,
    hardDisable: Boolean,
    min: { type: [String, Number], default: null },
    max: { type: [String, Number], default: null },
    removeSpinner: {
      type: Boolean,
      default: false,
    },
    showWordCount: { type: Boolean, default: false },
    labelStyles: { type: String, default: '' },
  },

  data: () => ({
    key: 1,
  }),

  computed: {
    isTextArea() {
      return this.type === 'textarea'
    },

    isError() {
      return this.status === 'error' || false
    },

    isSuccess() {
      return this.status === 'success' || false
    },

    isLoading() {
      return this.status === 'loading' || false
    },

    statusIconVisible() {
      return this.isError || this.isSuccess || this.isLoading
    },

    iconType() {
      if (this.isLoading) {
        return 'Spinner'
      }

      if (this.isError) {
        return 'ExclamationCircle'
      }

      return 'CheckCircle'
    },

    textInputStyles() {
      return {
        'pr-10': this.statusIconVisible,
        'border-green-300 text-green-900 placeholder-green-300 focus:border-green-300 focus:shadow-outline-green': this
          .isSuccess,
        'border-red-300 text-red-900 placeholder-red-300 focus:border-red-300 focus:shadow-outline-red': this
          .isError,
        'opacity-25 cursor-not-allowed': this.disabled,
      }
    },

    hideSpinnerClass() {
      if (!this.removeSpinner) return ''
      return 'arrow-hide'
    },
  },

  watch: {
    disabled(disabled) {
      if (!disabled) {
        this.focus()
      }
    },
  },

  mounted() {
    this.focus()
  },

  methods: {
    refresh() {
      this.key++

      this.focus()
    },

    async focus() {
      if (this.autofocus) {
        await this.$nextTick()

        this?.$refs?.input?.focus()
      }
    },

    focusInput() {
      this.$refs.input.focus()
    },

    handleBlur(e) {
      this.$emit('blur', e)
    },

    handleFocus(e) {
      this.$emit('focus', encodeURI)
    },

    handleEnter(e) {
      this.$emit('enter', e)
    },

    handleKeyupEnter(e) {
      this.$emit('enter', e)
    },

    handleInput(e) {
      this.$emit('input', e.target.value)
    },
  },
}
</script>
